import { useIntl } from "react-intl";
import { useQuery } from "@tanstack/react-query";
import {
  getAllTelegramBotConfig,
  getTelegramBotUsers,
} from "@lib/api/telegramBotConfigs";
import { useParamsManager } from "@lib/hooks/useParamsManager";
import AppTable from "@lib/components/MiniComponents/Table/AppTable";
import getColumns from "./colums";
import AppTableContentHeader from "@lib/components/MiniComponents/Table/AppTableContentHeader";
import AppsHeader from "@lib/components/AppComponents/AppsContainer/AppsHeader";
import { useEffect, useMemo, useState } from "react";
import { TableHeaderFilter } from "@lib/types/Api";
import AppsContent from "@lib/components/AppComponents/AppsContainer/AppsContent";
import { ITelegramBotUser } from "@lib/types/TelegramChatBot";
import { useNavigate } from "react-router-dom";
import { AppsContainer } from "@lib/components/AppComponents";
import { StyledFlex } from "@lib/components/MiniComponents/Table/index.styled";
import { Radio } from "antd";

const TelegramBotUsers = () => {
  const { messages } = useIntl();
  const navigate = useNavigate();

  const { params, setParams, handleRemoveFilters, onPageChange } =
    useParamsManager({
      page: 1,
      limit: 100,
      sort: "createdAt,DESC",
      join: ["manager"],
    });

  const [authFilter, setAuthFilter] = useState<boolean | null>(null);
  useEffect(() => {
    if (authFilter === null) {
      handleRemoveFilters(["managerId"]);
    } else {
      setParams({
        filter: [authFilter ? `managerId||$notnull` : `managerId||$isnull`],
      });
    }
  }, [authFilter]);

  const telegramBotUsersQuery = useQuery({
    queryKey: ["telegram-bot-users", JSON.stringify(params)],
    queryFn: () => getTelegramBotUsers(params),
    enabled: !!params,
  });
  const { data, isFetching, isLoading } = telegramBotUsersQuery;
  const apiData = data?.data?.data;

  const { data: telegramBotConfigsData } = useQuery({
    queryKey: ["telegram-bot-config-list"],
    queryFn: () =>
      getAllTelegramBotConfig({
        limit: 100,
        page: 1,
        sort: "createdAt,DESC",
      }),
    enabled: !!params,
  });

  const columns = getColumns({
    telegramBotConfigs: telegramBotConfigsData?.data?.data,
    messages,
    navigate,
    apiData,
  });

  const filters = useMemo(() => {
    const ignoreKeys = [];
    if (authFilter !== null) {
      ignoreKeys.push("managerId");
    }

    if (!params?.filter) return [];

    return params?.filter
      .map((f) => ({
        key: f.split("||")[0],
        title: columns.find((c) => c?.key === f.split("||")[0])?.title || "",
        operator: f.split("||")[1],
        value: f.split("||")[2],
      }))
      .filter((f) => !ignoreKeys.includes(f.key)) as TableHeaderFilter[];
  }, [params, authFilter]);

  return (
    <AppsContainer
      fullView={true}
      title={messages["sidebar.telegramBotUsers"] as string}
      cardProps={{
        borderFull: true,
      }}
    >
      <AppsHeader>
        <StyledFlex>
          <Radio.Group
            value={authFilter}
            onChange={(e) => {
              if (e.target.value === null) {
                setAuthFilter(null);
              } else {
                setAuthFilter(e.target.value);
              }
            }}
          >
            <Radio value={null}>{messages["common.all"] as string}</Radio>
            <Radio value={true}>
              {messages["common.authorizedUsers"] as string}
            </Radio>
            <Radio value={false}>
              {messages["common.unauthorizedUsers"] as string}
            </Radio>
          </Radio.Group>
        </StyledFlex>
      </AppsHeader>

      <AppsHeader>
        <AppTableContentHeader
          showInfo
          page={params?.page || 1}
          perPage={params?.limit || 100}
          onPageChange={onPageChange}
          query={telegramBotUsersQuery}
          filters={filters}
          handleRemoveFilters={handleRemoveFilters}
        />
      </AppsHeader>

      <AppsContent>
        <AppTable<ITelegramBotUser>
          dataSource={apiData}
          columns={columns}
          rowKey={"id"}
          loading={isLoading || isFetching}
        />
      </AppsContent>
    </AppsContainer>
  );
};

export default TelegramBotUsers;
